/* eslint-disable max-lines */

import {
  enrollAsDeveloperForMicrosoftStore,
  idAtXboxUrl,
  idXboxAccelerationProgramUrl,
  liveXblOverviewUrl,
  uwpStorePoliciesUrl,
} from "../../../constants";
import { getImage, ImageType } from "../../../helpers/imageHelper";
import { useStrings } from "../../../localization/localizationHelpers";
import { OnboardingUrls } from "../../../partnerConfig/routes";

export type ProgramProps = {
  title: string;
  description: string;
  badges: string[];
  callToAction: { text: string; url: string; isExternalUrl?: boolean };
  image: { url: string; alt: string };
  faqs: { question: string; answer: string }[];
  cards: {
    image: { url: string; alt: string };
    title: string;
    description: string;
    link?: { url: string; text: string; ariaLabel: string };
  }[];
};

export const useIdXboxProgramLandingPageProgramConfig = (): ProgramProps => {
  const [
    landingPageIDXboxImageAltText,
    landingPageIDXboxTitle,
    landingPageIDXboxBadgeIndependentGameDeveloper,
    landingPageIDXboxBadgeIndependentStudios,
    landingPageIDXboxDescription,
    landingPageIDXboxCTA,
    landingPageIDXboxBlog,
    landingPageIDXboxBlogDescription,
    landingPageIDXboxBlogImageAltText,
    landingPageIDXboxBlogCTA,
    landingPageIDXboxAccelerationProgramTitle,
    landingPageIDXboxAccelerationProgramDescription,
    landingPageIDXboxAccelerationProgramImageAltText,
    landingPageIDXboxAccelerationProgramAccessibilityForButton,
    learnMore,
    landingPageIDXboxFAQ1Question,
    landingPageIDXboxFAQ1Answer,
    landingPageIDXboxFAQ2Question,
    landingPageIDXboxFAQ2Answer,
    landingPageIDXboxFAQ3Question,
    landingPageIDXboxFAQ3Answer,
    landingPageViewTheBlogIdXboxProgram,
  ] = useStrings(
    "landingPageIDXboxImageAltText",
    "landingPageIDXboxTitle",
    "landingPageIDXboxBadgeIndependentGameDeveloper",
    "landingPageIDXboxBadgeIndependentStudios",
    "landingPageIDXboxDescription",
    "landingPageIDXboxCTA",
    "landingPageIDXboxBlog",
    "landingPageIDXboxBlogDescription",
    "landingPageIDXboxBlogImageAltText",
    "landingPageIDXboxBlogCTA",
    "landingPageIDXboxAccelerationProgramTitle",
    "landingPageIDXboxAccelerationProgramDescription",
    "landingPageIDXboxAccelerationProgramImageAltText",
    "landingPageIDXboxAccelerationProgramAccessibilityForButton",
    "learnMore",
    "landingPageIDXboxFAQ1Question",
    "landingPageIDXboxFAQ1Answer",
    "landingPageIDXboxFAQ2Question",
    "landingPageIDXboxFAQ2Answer",
    "landingPageIDXboxFAQ3Question",
    "landingPageIDXboxFAQ3Answer",
    "landingPageViewTheBlogIdXboxProgram"
  );
  return {
    title: landingPageIDXboxTitle,
    description: landingPageIDXboxDescription,
    badges: [
      landingPageIDXboxBadgeIndependentGameDeveloper,
      landingPageIDXboxBadgeIndependentStudios,
    ],
    callToAction: {
      text: landingPageIDXboxCTA,
      url: OnboardingUrls.ProgramSelection,
    },
    image: {
      url: getImage("idxbox", ImageType.Jpg),
      alt: landingPageIDXboxImageAltText,
    },
    faqs: [
      {
        question: landingPageIDXboxFAQ1Question,
        answer: landingPageIDXboxFAQ1Answer,
      },
      {
        question: landingPageIDXboxFAQ2Question,
        answer: landingPageIDXboxFAQ2Answer,
      },
      {
        question: landingPageIDXboxFAQ3Question,
        answer: landingPageIDXboxFAQ3Answer,
      },
    ],
    cards: [
      {
        image: {
          url: getImage("onboarding_landing_idxbox1", ImageType.Jpg),
          alt: landingPageIDXboxBlogImageAltText,
        },
        title: landingPageIDXboxBlog,
        description: landingPageIDXboxBlogDescription,
        link: {
          text: landingPageIDXboxBlogCTA,
          url: idAtXboxUrl,
          ariaLabel: landingPageViewTheBlogIdXboxProgram,
        },
      },
      {
        image: {
          url: getImage("dap_hub", ImageType.Png),
          alt: landingPageIDXboxAccelerationProgramImageAltText,
        },
        title: landingPageIDXboxAccelerationProgramTitle,
        description: landingPageIDXboxAccelerationProgramDescription,
        link: {
          text: learnMore,
          url: idXboxAccelerationProgramUrl,
          ariaLabel: landingPageIDXboxAccelerationProgramAccessibilityForButton,
        },
      },
    ],
  };
};

export const useCreatorsProgramLandingPageProgramConfig = (): ProgramProps => {
  const [
    landingPageCreatorsProgramTitle,
    landingPageCreatorsProgramBadgeStudents,
    landingPageCreatorsProgramBadgeHobbyist,
    landingPageCreatorsProgramDescription,
    landingPageCreatorsProgramCTA,
    landingPageCreatorsProgramCard1Title,
    landingPageCreatorsProgramCard1Description,
    landingPageCreatorsProgramCard2Title,
    landingPageCreatorsProgramCard2Description,
    landingPageCreatorsProgramCardsCTA,
    landingPageCreatorsProgramFAQ1Question,
    landingPageCreatorsProgramFAQ1Answer,
    landingPageCreatorsProgramFAQ2Question,
    landingPageCreatorsProgramFAQ2Answer,
    landingPageCreatorsProgramFAQ3Question,
    landingPageCreatorsProgramFAQ3Answer,
    landingPageCreatorsProgramFAQ4Question,
    landingPageCreatorsProgramFAQ4Answer,
    landingPageCreatorsProgramFAQ5Question,
    landingPageCreatorsProgramFAQ5Answer,
    landingPageCreatorsProgramFAQ6Question,
    landingPageCreatorsProgramFAQ6Answer,
    landingPageCreatorsProgramFAQ7Question,
    landingPageCreatorsProgramFAQ7Answer,
    landingPageCreatorsProgramFAQ8Question,
    landingPageCreatorsProgramFAQ8Answer,
    landingPageLearnMoreSimplifiedTestingAndDeploymentCreatorsProgramCTA,
    landingPageLearnMoreThePowerOfXboxServicesCreatorsProgramCTA,
  ] = useStrings(
    "landingPageCreatorsProgramTitle",
    "landingPageCreatorsProgramBadgeStudents",
    "landingPageCreatorsProgramBadgeHobbyist",
    "landingPageCreatorsProgramDescription",
    "landingPageCreatorsProgramCTA",
    "landingPageCreatorsProgramCard1Title",
    "landingPageCreatorsProgramCard1Description",
    "landingPageCreatorsProgramCard2Title",
    "landingPageCreatorsProgramCard2Description",
    "landingPageCreatorsProgramCardsCTA",
    "landingPageCreatorsProgramFAQ1Question",
    "landingPageCreatorsProgramFAQ1Answer",
    "landingPageCreatorsProgramFAQ2Question",
    "landingPageCreatorsProgramFAQ2Answer",
    "landingPageCreatorsProgramFAQ3Question",
    "landingPageCreatorsProgramFAQ3Answer",
    "landingPageCreatorsProgramFAQ4Question",
    "landingPageCreatorsProgramFAQ4Answer",
    "landingPageCreatorsProgramFAQ5Question",
    "landingPageCreatorsProgramFAQ5Answer",
    "landingPageCreatorsProgramFAQ6Question",
    "landingPageCreatorsProgramFAQ6Answer",
    "landingPageCreatorsProgramFAQ7Question",
    "landingPageCreatorsProgramFAQ7Answer",
    "landingPageCreatorsProgramFAQ8Question",
    "landingPageCreatorsProgramFAQ8Answer",
    "landingPageLearnMoreSimplifiedTestingAndDeploymentCreatorsProgramCTA",
    "landingPageLearnMoreThePowerOfXboxServicesCreatorsProgramCTA"
  );
  return {
    title: landingPageCreatorsProgramTitle,
    description: landingPageCreatorsProgramDescription,
    badges: [landingPageCreatorsProgramBadgeStudents, landingPageCreatorsProgramBadgeHobbyist],
    callToAction: {
      text: landingPageCreatorsProgramCTA,
      url: enrollAsDeveloperForMicrosoftStore,
      isExternalUrl: true,
    },
    image: {
      url: getImage("onboarding_landing_creators1", ImageType.Jpg),
      alt: landingPageCreatorsProgramDescription,
    },
    faqs: [
      {
        question: landingPageCreatorsProgramFAQ1Question,
        answer: landingPageCreatorsProgramFAQ1Answer,
      },
      {
        question: landingPageCreatorsProgramFAQ2Question,
        answer: landingPageCreatorsProgramFAQ2Answer,
      },
      {
        question: landingPageCreatorsProgramFAQ3Question,
        answer: landingPageCreatorsProgramFAQ3Answer,
      },
      {
        question: landingPageCreatorsProgramFAQ4Question,
        answer: landingPageCreatorsProgramFAQ4Answer,
      },
      {
        question: landingPageCreatorsProgramFAQ5Question,
        answer: landingPageCreatorsProgramFAQ5Answer,
      },
      {
        question: landingPageCreatorsProgramFAQ6Question,
        answer: landingPageCreatorsProgramFAQ6Answer,
      },
      {
        question: landingPageCreatorsProgramFAQ7Question,
        answer: landingPageCreatorsProgramFAQ7Answer,
      },
      {
        question: landingPageCreatorsProgramFAQ8Question,
        answer: landingPageCreatorsProgramFAQ8Answer,
      },
    ],
    cards: [
      {
        image: {
          url: getImage("onboarding_landing_creators3", ImageType.Jpg),
          alt: landingPageCreatorsProgramCard1Title,
        },
        title: landingPageCreatorsProgramCard1Title,
        description: landingPageCreatorsProgramCard1Description,
        link: {
          text: landingPageCreatorsProgramCardsCTA,
          url: uwpStorePoliciesUrl,
          ariaLabel: landingPageLearnMoreSimplifiedTestingAndDeploymentCreatorsProgramCTA,
        },
      },
      {
        image: {
          url: getImage("onboarding_landing_creators2", ImageType.Jpg),
          alt: landingPageCreatorsProgramCard2Title,
        },
        title: landingPageCreatorsProgramCard2Title,
        description: landingPageCreatorsProgramCard2Description,
        link: {
          text: landingPageCreatorsProgramCardsCTA,
          url: liveXblOverviewUrl,
          ariaLabel: landingPageLearnMoreThePowerOfXboxServicesCreatorsProgramCTA,
        },
      },
    ],
  };
};
